import React, { useContext } from "react";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { Info, CheckCircle, ChevronLeft, ChevronRight, HelpCircle, ShieldCheck, X } from "lucide-react";
import { IntlContext } from "context";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { Radio, TextField } from "components/Form/Field";
import Button from "ui/Button";
import { useRegisterStepper } from "../common/RegisterStepper";
import { useRegisterDraftGuard } from "../common/useRegisterDraftGuard";
import type { RegisterDraft } from "../types";
import { validateRegisterProviderCesu } from "hooks/register";
import { getErrorResponseData } from "helpers/typing";
import { trackRegisterStepComplete } from "lib/analytics/dataLayer";
import CesuRadioOptionLabel, {
    cesuRadioGroupClassName,
    cesuRadioLabelClassName,
} from "components/Cesu/CesuRadioOptionLabel";
import { fieldLabelWithTooltip } from "../common/FieldInfoTooltip";

interface Props {
    provider: RegisterDraft | undefined;
    setProvider: (provider: RegisterDraft | undefined) => void;
}

const RegisterProviderCesu: React.FC<Props> = ({ provider, setProvider }) => {
    const intl = useContext(IntlContext);
    const stepper = useRegisterStepper();
    useRegisterDraftGuard(provider, "prestations");

    const onSubmit = async (values: RegisterDraft) => {
        try {
            const data = await validateRegisterProviderCesu(values);
            setProvider(Object.assign({}, provider, data));
            trackRegisterStepComplete({
                registerType: "provider",
                stepKey: "cesu",
                stepIndex: 6,
                stepLabel: intl.formatMessage("authentication.register.provider_flow.cesu.label"),
            });
            stepper.goTo("iban");
        } catch (err: unknown) {
            const errData = getErrorResponseData(err);
            if (errData) {
                return formatErrorResponse(errData);
            }
            throw err;
        }
    };

    return (
        <React.Fragment>
            <Form
                onSubmit={onSubmit}
                initialValues={{
                    ...(provider ?? {}),
                    has_cesu:
                        (provider as RegisterDraft | undefined)?.has_cesu === undefined ||
                        (provider as RegisterDraft | undefined)?.has_cesu === null ||
                        String((provider as RegisterDraft | undefined)?.has_cesu ?? "").trim() === ""
                            ? "0"
                            : (provider as RegisterDraft | undefined)?.has_cesu,
                } as RegisterDraft}
                mutators={{ ...arrayMutators }}
                render={({ handleSubmit, submitting, values }) => (
                    <form onSubmit={handleSubmit} className="register__form">
                        <div className="flex flex--direction-column gap-10">
                            <p className="h1 font-black">
                                Cadre légal & <span className="block text-primary">protection sociale.</span>
                            </p>
                            <p className="h4 margin-bottom--2 margin-top--1">
                                Twoghether utilise le CESU pour vous garantir un travail déclaré et sécurisé.
                            </p>

                            <div className="register__group-fields">
                                <div className="grid--12 register__grid grid--has-gutter-3x">
                                    <div className="grid__col--12 grid__col--small-12">
                                        <div className="w-full rounded-[28px] border border-primary/10 bg-primary/5 p-6 sm:p-7">
                                            <div className="flex items-start gap-3">
                                                <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-white text-primary shadow-sm ring-1 ring-border/40">
                                                    <HelpCircle className="h-4 w-4" />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="text-sm font-black">C'est quoi le CESU ?</div>
                                                    <div className="mt-2 text-sm text-muted-foreground leading-relaxed">
                                                        Le Chèque Emploi Service Universel est un dispositif de l'URSSAF qui
                                                        simplifie les démarches pour les services à la personne. C'est
                                                        l'assurance d'être <span className="text-primary font-semibold">100% légal</span>, d'avoir une{" "}
                                                        <span className="text-primary font-semibold">couverture santé</span>, de cotiser pour votre{" "}
                                                        <span className="text-primary font-semibold">retraite</span> et d'être assuré en cas d'accident.
                                                    </div>
                                                </div>
                                                <div className="hidden sm:block opacity-20">
                                                    <ShieldCheck className="h-12 w-12 text-primary" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid__col--12 grid__col--small-12">
                                        <div className="text-xs font-black uppercase tracking-wider text-muted-foreground">
                                            Avez-vous déjà un numéro CESU salarié ?
                                        </div>
                                    </div>

                                    <div className="grid__col--12 grid__col--small-12">
                                        <Radio
                                            inputProps={{
                                                name: "has_cesu",
                                                required: true,
                                                className: "sr-only",
                                                groupClassName: cesuRadioGroupClassName,
                                                labelClassName: cesuRadioLabelClassName,
                                                options: [
                                                    {
                                                        value: "1",
                                                        label: (
                                                            <CesuRadioOptionLabel
                                                                icon={<CheckCircle className="h-4 w-4" />}
                                                                title="Oui, j'en ai un"
                                                                subtitle="Je possède déjà mon numéro national de salarié URSSAF."
                                                            />
                                                        ),
                                                    },
                                                    {
                                                        value: "0",
                                                        label: (
                                                            <CesuRadioOptionLabel
                                                                icon={<X className="h-4 w-4" />}
                                                                title="Non, pas encore"
                                                                subtitle="Il me sera attribué automatiquement lors de ma 1ère prestation déclarée."
                                                            />
                                                        ),
                                                    },
                                                ],
                                            }}
                                            fieldControlProps={{
                                                className: "grid gap-4 sm:grid-cols-2",
                                            }}
                                        />
                                    </div>

                                    {String((values as RegisterDraft)?.has_cesu ?? "") === "1" && (
                                        <div className="grid__col--12 grid__col--small-12">
                                            <TextField
                                                inputProps={{
                                                    name: "num_cesu",
                                                    required: true,
                                                    placeholder: "XXX XXX XXX XXX",
                                                    minLength: 14,
                                                    maxLength: 14,
                                                }}
                                                labelProps={{
                                                    label: fieldLabelWithTooltip(
                                                        "N° CESU",
                                                        intl.formatMessage("register.label.provider.num_cesu.tooltip"),
                                                    ),
                                                }}
                                            />
                                            <div className="mt-2 text-xs text-muted-foreground">
                                                Vous pouvez retrouver votre n° CESU soit dans votre espace personnel sur le site du CESU, soit sur la dernière fiche de paie éditée par l'URSSAF.
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="w-full rounded-[28px] border border-primary/10 bg-primary/5 px-6 py-5 text-sm text-muted-foreground">
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-primary shadow-sm ring-1 ring-border/40">
                                        <Info className="h-4 w-4" />
                                    </div>
                                    <p className="min-w-0 leading-relaxed">
                                        <span className="font-black text-foreground/90">Rassurez-vous :</span>{" "}
                                        Twoghether s'occupe de faire le lien avec l'URSSAF. Vous n'avez aucune démarche
                                        administrative complexe à faire.
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center justify-between text-center margin-top--10">
                                <Button
                                    className="btn inline-flex items-center justify-center gap-3 rounded-full px-0 py-4 font-black text-muted-foreground/70"
                                    onClick={() => {
                                        setProvider(Object.assign({}, provider, values));
                                        stepper.goTo("photo");
                                    }}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    {intl.formatMessage("generic.step.previous")}
                                </Button>
                                <Button
                                    className="btn btn--primary inline-flex items-center justify-center gap-3 rounded-full px-10 py-4 font-black"
                                    type="submit"
                                    disabled={submitting || !values?.has_cesu || (values?.has_cesu === "1" && !values?.num_cesu)}
                                >
                                    {intl.formatMessage("generic.step.next")}
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>

                    </form>
                )}
            />
        </React.Fragment>
    );
};

export default RegisterProviderCesu;
